import { Children, useCallback } from 'react';
import type { ReactElement, ReactNode } from 'react';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { InfoCardVariants, MarkdownContent } from '@backstage/core-components';
import {
  alertApiRef,
  errorApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityRefLinks,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';

const useCardStyles = makeStyles({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)',
    marginBottom: '10px',
  },
  fullHeightCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  gridItemCardContent: {
    flex: 1,
  },
  fullHeightCardContent: {
    flex: 1,
  },
  description: {
    wordBreak: 'break-word',
  },
});

const useFieldStyles = makeStyles(theme => ({
  value: {
    fontWeight: 'bold',
    overflow: 'hidden',
    lineHeight: '24px',
    wordBreak: 'break-word',
  },
  label: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: '10px',
    fontWeight: 'bold',
    letterSpacing: 0.5,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
}));

type AboutFieldProps = {
  label: string;
  value?: string;
  gridSizes?: Record<string, number>;
  children?: ReactNode;
  className?: string;
};

/**
 * Renders a single labelled field within the About card.
 * Mirrors the layout of the upstream Backstage `AboutField` component so
 * the custom card looks visually identical to the default one. Falls back to
 * an "Unknown" placeholder when no children or value are supplied, matching
 * the behaviour users expect from the default card.
 *
 * @param props - Field props.
 * @returns A `Grid` item containing a label and either children or a value.
 */
function AboutField(props: AboutFieldProps): ReactElement {
  const { label, value, gridSizes, children, className } = props;
  const classes = useFieldStyles();
  const hasChildren =
    Children.count(children) > 0 &&
    Children.toArray(children).some(child => Boolean(child));
  const content = hasChildren ? (
    children
  ) : (
    <Typography variant="body2" className={classes.value}>
      {value || 'Unknown'}
    </Typography>
  );
  return (
    <Grid item {...gridSizes} className={className}>
      <Typography variant="h2" className={classes.label}>
        {label}
      </Typography>
      {content}
    </Grid>
  );
}

export type CustomAboutCardProps = {
  variant?: InfoCardVariants;
};

/**
 * Drop-in replacement for `EntityAboutCard` from `@backstage/plugin-catalog`
 * that omits the "Domain" field. The default card renders Description, Owner,
 * Domain, System, Parent Component, Type, Lifecycle and Tags depending on the
 * entity kind; this implementation keeps every field except Domain so we can
 * stop surfacing domain relations on entity overview pages.
 *
 * Behaviour and styling mirror the upstream component closely: it preserves
 * the gridItem/fullHeight variants, the refresh and edit icon buttons in the
 * card header, and the same field ordering, so visually it looks identical
 * apart from the missing Domain row.
 *
 * @param props - Card props, including the optional layout variant.
 * @returns A Material UI card displaying the entity summary.
 */
export function CustomAboutCard(
  props: CustomAboutCardProps,
): ReactElement {
  const { variant } = props;
  const classes = useCardStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);

  const kind = entity.kind.toLocaleLowerCase('en-US');
  const isResource = kind === 'resource';
  const isComponent = kind === 'component';
  const isAPI = kind === 'api';
  const isTemplate = kind === 'template';
  const isLocation = kind === 'location';
  const isGroup = kind === 'group';

  const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const partOfComponentRelations = getEntityRelations(
    entity,
    RELATION_PART_OF,
    { kind: 'component' },
  );
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

  const entityMetadataEditUrl =
    entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      errorApi.post(e as Error);
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  let cardClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
  }

  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardContentClass = classes.fullHeightCardContent;
  }

  const tags = entity.metadata.tags ?? [];
  const showTypeField =
    isAPI ||
    isComponent ||
    isResource ||
    isTemplate ||
    isGroup ||
    isLocation ||
    typeof entity.spec?.type === 'string';
  const showLifecycleField =
    isAPI || isComponent || typeof entity.spec?.lifecycle === 'string';
  const showSystemField =
    isAPI ||
    isComponent ||
    isResource ||
    partOfSystemRelations.length > 0;

  return (
    <Card className={cardClass}>
      <CardHeader
        title="About"
        action={
          <>
            {allowRefresh && (
              <IconButton
                aria-label="Refresh"
                title="Schedule entity refresh"
                onClick={refreshEntity}
              >
                <CachedIcon />
              </IconButton>
            )}
            <IconButton
              aria-label="Edit"
              disabled={!entityMetadataEditUrl}
              title="Edit Metadata"
              href={entityMetadataEditUrl ?? '#'}
            >
              <EditIcon />
            </IconButton>
          </>
        }
      />
      <Divider />
      <CardContent className={cardContentClass}>
        <Grid container>
          <AboutField label="Description" gridSizes={{ xs: 12 }}>
            <MarkdownContent
              className={classes.description}
              content={entity.metadata.description || 'No description'}
            />
          </AboutField>
          <AboutField
            label="Owner"
            value="No Owner"
            className={classes.description}
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          >
            {ownedByRelations.length > 0 && (
              <EntityRefLinks
                entityRefs={ownedByRelations}
                defaultKind="group"
              />
            )}
          </AboutField>
          {showSystemField && (
            <AboutField
              label="System"
              value="No System"
              gridSizes={{ xs: 12, sm: 6, lg: 4 }}
            >
              {partOfSystemRelations.length > 0 && (
                <EntityRefLinks
                  entityRefs={partOfSystemRelations}
                  defaultKind="system"
                />
              )}
            </AboutField>
          )}
          {isComponent && partOfComponentRelations.length > 0 && (
            <AboutField
              label="Parent Component"
              value="No Parent Component"
              gridSizes={{ xs: 12, sm: 6, lg: 4 }}
            >
              <EntityRefLinks
                entityRefs={partOfComponentRelations}
                defaultKind="component"
              />
            </AboutField>
          )}
          {showTypeField && (
            <AboutField
              label="Type"
              value={entity.spec?.type as string | undefined}
              gridSizes={{ xs: 12, sm: 6, lg: 4 }}
            />
          )}
          {showLifecycleField && (
            <AboutField
              label="Lifecycle"
              value={entity.spec?.lifecycle as string | undefined}
              gridSizes={{ xs: 12, sm: 6, lg: 4 }}
            />
          )}
          <AboutField
            label="Tags"
            value="No Tags"
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          >
            {tags.map(tag => (
              <Chip key={tag} size="small" label={tag} />
            ))}
          </AboutField>
        </Grid>
      </CardContent>
    </Card>
  );
}
